import React, { Fragment } from "react";

import AppPlaceholderPhoto from "@common/assets/images/shared-icons/app-icon-placeholder.svg";
import ChromeAppPlaceholderPhoto from "@common/assets/images/shared-icons/purple-app-icon-placeholder.svg";
import { ReactComponent as UserFeedbackIcon } from "@common/assets/images/sidebar-icons/user-feedback-icon.svg";
import { DropdownItem } from "@common/components/Dropdown/Dropdown";
import * as StyledDropdown from "@common/components/Dropdown/Dropdown.styles";
import Paragraph from "@common/components/Paragraph/Paragraph";
import { App } from "@common/models/App.model";
import { isAdmin, isMember } from "@common/models/Member.model";
import { vars } from "@common/styles";
import { Flex } from "@common/styles/reusable/Flex/Flex.styles";
import { getOSImage } from "@common/util/contentResolvers";
import identifiers from "@common/util/identifiers.json";
import { t } from "@common/util/translator";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useUnreadNotificationsContext } from "@main/context/App/UnreadNotificationsContext";
import { RoutePaths } from "@main/router/config/routePaths";
import { generatePath, useNavigate } from "react-router-dom";

import * as StyledAppList from "./AppListDropdown.styles";

interface AppListDropdownProps {
  appList: Array<App>;
  onAppSwitch?: (app: App) => void;
  disabled?: boolean;
  displayNotifications?: boolean;
  isDropdownMenuOpen: boolean;
  setIsDropdownMenuOpen: (isDropdownMenuOpen: boolean) => void;
}

export default function AppListDropdown({
  appList,
  onAppSwitch,
  isDropdownMenuOpen,
  setIsDropdownMenuOpen,
}: AppListDropdownProps) {
  const navigate = useNavigate();
  const { selectedApp, selectedWorkspace } = useAppSelectionContext();
  const { unreadNotifications } = useUnreadNotificationsContext();

  return (
    <>
      <StyledAppList.AppListDropdown>
        <Flex
          $alignItems="center"
          style={{ height: "100%" }}
        >
          <StyledDropdown.DropdownMenu
            $active={isDropdownMenuOpen}
            scroll={scroll}
            $position="left"
            $wide
            $button={false}
            className="dropdown-menu"
          >
            <StyledDropdown.DropdownScroll
              cx={StyledAppList.dropdownScroll}
              data-testid={identifiers["navbar.selectedApp.dropdown.list"]}
            >
              <Flex
                $gap={0.6}
                style={{ margin: "0 1rem" }}
                $alignItems="center"
              >
                <StyledAppList.AppOptionsContainer
                  onClick={() =>
                    navigate(
                      generatePath(RoutePaths.USER_FEEDBACK, {
                        workspaceSlug: selectedWorkspace?.slug ?? null,
                        appKey: selectedApp?.key ?? null,
                      }),
                    )
                  }
                >
                  <UserFeedbackIcon
                    height={24}
                    width={24}
                  />
                  See all tickets
                </StyledAppList.AppOptionsContainer>
                <StyledAppList.AppOptionsContainer
                  onClick={() =>
                    navigate(
                      generatePath(RoutePaths.ADMINISTRATION_APPS_GENERAL_KEY, {
                        workspaceSlug: selectedWorkspace?.slug ?? null,
                        appKey: selectedApp?.key ?? null,
                      }),
                    )
                  }
                >
                  <StyledAppList.PermissionIcon />
                  App settings
                </StyledAppList.AppOptionsContainer>
                <StyledAppList.AppOptionsContainer
                  onClick={() =>
                    navigate(
                      generatePath(RoutePaths.ADD_APP, {
                        workspaceSlug: selectedWorkspace?.slug ?? null,
                      }),
                    )
                  }
                >
                  <StyledAppList.Plus />
                  Add new app
                </StyledAppList.AppOptionsContainer>
              </Flex>

              {Boolean(appList.length) && (isAdmin(selectedWorkspace?.role) || isMember(selectedWorkspace?.role)) && (
                <Fragment>
                  <StyledDropdown.DropdownDivider />

                  <StyledDropdown.SwitchAppDropdownHeading>
                    {t("header.app.dropdown.switchApp")}
                  </StyledDropdown.SwitchAppDropdownHeading>
                </Fragment>
              )}

              {Boolean(appList.length) && (
                <Fragment>
                  {appList.map((app) => {
                    return (
                      <DropdownItem
                        key={app.id}
                        onClick={() => {
                          setIsDropdownMenuOpen(!isDropdownMenuOpen);
                          onAppSwitch?.(app);
                        }}
                      >
                        <Flex $alignItems="center">
                          <img
                            src={
                              app.logo_url
                                ? app.logo_url
                                : app.is_extension
                                ? ChromeAppPlaceholderPhoto
                                : AppPlaceholderPhoto
                            }
                            width={28}
                            height={28}
                            style={{
                              borderRadius: "0.8rem",
                              marginRight: "0.8rem",
                              objectFit: "cover",
                            }}
                          />
                          <StyledAppList.Name>{app.name}</StyledAppList.Name>
                          <img
                            src={getOSImage(app.platform)}
                            width={20}
                            height={20}
                          />
                          {unreadNotifications[app.id] && unreadNotifications[app.id].length && (
                            <StyledAppList.NotificationIndicator>
                              <Paragraph
                                color={vars.colors.white}
                                fontSize={12}
                              >
                                {unreadNotifications[app.id].length > 99 ? "99" : unreadNotifications[app.id].length}
                              </Paragraph>
                            </StyledAppList.NotificationIndicator>
                          )}
                        </Flex>
                      </DropdownItem>
                    );
                  })}
                </Fragment>
              )}
            </StyledDropdown.DropdownScroll>
          </StyledDropdown.DropdownMenu>
        </Flex>
      </StyledAppList.AppListDropdown>
      <StyledAppList.Overlay
        $active={isDropdownMenuOpen}
        onClick={() => setIsDropdownMenuOpen(false)}
      />
    </>
  );
}
